import React, { useState } from 'react';
import { Autocomplete, Icons } from '@ublend-npm/aulaui-core';
import SearchListItem from './SearchListItem';
import { SpaceUser } from '../../Conversion.types';
import { ADD_TO_EXTENSION_TOOLTIP } from '../../constants';

interface DatasetItem {
  id: string;
  label: string;
  email: string;
  avatar: string | undefined;
}

const normalizeUsersList = (users: SpaceUser[] = []) => {
  return users?.map(({ id, fullName, avatar, email }) => ({
    id,
    avatar,
    email,
    label: fullName,
  }));
};

const getUsersSuggestionsList = (
  users: DatasetItem[],
  selectedUsers: DatasetItem[]
) => {
  return users?.filter(
    (user) =>
      !selectedUsers?.find((selectedUser) => selectedUser?.id === user?.id)
  );
};

const onInputChange = (
  availableUsers: DatasetItem[],
  setDataSet: (data: DatasetItem[]) => void,
  query: string
) => {
  if (!query) {
    return setDataSet(availableUsers);
  }

  return setDataSet(
    availableUsers.filter(
      ({ label }) => label.toLowerCase().indexOf(query.toLowerCase()) !== -1
    )
  );
};

type SearchBarProps = {
  users: SpaceUser[];
  selectedUsers: SpaceUser[];
  onChange: (selectedStudents: SpaceUser[]) => void;
  greyedOutUsers?: string[];
  isFetching?: boolean;
};

const SearchBar = ({
  users,
  selectedUsers,
  isFetching = false,
  onChange,
  greyedOutUsers = [],
}: SearchBarProps) => {
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const normalizedUsers = normalizeUsersList(users);
  const availableUsers = getUsersSuggestionsList(
    normalizedUsers as DatasetItem[],
    selectedUsers as DatasetItem[]
  );
  const [dataSet, setDataSet] = useState<DatasetItem[]>(availableUsers);
  const [query, setQuery] = useState<string>('');

  const onSelect = (student: DatasetItem) => {
    onChange([
      ...selectedUsers,
      {
        id: student.id,
        avatar: student.avatar,
        email: student.email,
        fullName: student.label,
      },
    ]);
    setOpenDropdown(false);
    setQuery('');
  };

  const isDisabled = (id: string) => {
    return greyedOutUsers.includes(id);
  };

  const getUserInformation = () => {
    return (
      <>
        <div>This student can&#39;t be added because they already</div>
        <div>have a submission in the destination assignment</div>
      </>
    );
  };

  return (
    <Autocomplete
      value={query}
      open={openDropdown}
      id="search-bar"
      freeSolo={true as any} // causing a typescript error for freeSolo so added any to bypass it
      label="Search for students"
      placeholder="Search for students"
      options={dataSet}
      limitRows={10}
      loading={isFetching}
      disabled={isFetching}
      getOptionLabel={(option: DatasetItem | string) =>
        typeof option === 'string' ? option : option.label
      }
      onClose={(_, reason) => {
        if (reason === 'blur' || reason === 'toggleInput') {
          setOpenDropdown(false);
        }
      }}
      sx={{
        '& .MuiAutocomplete-option': {
          padding: '0.5rem 1rem',
        },
      }}
      isOptionEqualToValue={(option: DatasetItem, value: DatasetItem) =>
        option.id === value.id
      }
      onInputChange={(_, value: string) => {
        if (value?.length > 0) {
          setQuery(value);
          setOpenDropdown(true);
          onInputChange(availableUsers, setDataSet, value);
        } else {
          setOpenDropdown(false);
        }
      }}
      onChange={(_, value) => {
        if (
          value &&
          typeof value !== 'string' &&
          !isDisabled((value as DatasetItem).id)
        ) {
          onSelect(value as DatasetItem);
        }
      }}
      renderOption={(
        props,
        option: { id: string; label: string; email: string; avatar: string }
      ) => {
        const disabled = isDisabled(option.id);

        return (
          <li
            {...(props as React.HTMLAttributes<HTMLLIElement>)}
            key={option.id}
            aria-disabled={disabled}
            onClick={(event) => {
              if (disabled) {
                event.preventDefault();
                event.stopPropagation();
                return;
              }
              props.onClick?.(event);
            }}
          >
            <SearchListItem
              fullName={option.label}
              email={option.email}
              avatar={option.avatar}
              actionIcon={!disabled ? Icons.AddCircle : null}
              isDisabled={disabled}
              tooltip={!disabled ? ADD_TO_EXTENSION_TOOLTIP : undefined}
              disabledText={disabled ? getUserInformation() : undefined}
            />
          </li>
        );
      }}
    />
  );
};

export default React.memo(SearchBar);
